import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "@/components/ui/sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { StatCard, downloadBlob } from "@/components/admin/shared";
import { STATUS_LABELS } from "@/components/admin/applications/StatusControls";
import { ApplicationsFilters } from "@/components/admin/applications/ApplicationsFilters";
import { ApplicationsTable } from "@/components/admin/applications/ApplicationsTable";
import { ApplicationDetailDialog, CvPreviewDialog } from "@/components/admin/applications/ApplicationDialogs";

const buildParams = (filters, sort) => {
  const p = { sort: sort.field, order: sort.order };
  if (filters.tipologia !== "tutte") p.tipologia = filters.tipologia;
  if (filters.status !== "tutti") p.status = filters.status;
  if (filters.highOnly) p.media_min = 27;
  if (filters.q.trim()) p.q = filters.q.trim();
  return p;
};

export const ApplicationsPanel = ({ onUnauthorized }) => {
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ tipologia: "tutte", status: "tutti", highOnly: false, q: "" });
  const [sort, setSort] = useState({ field: "created_at", order: "desc" });
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const params = useMemo(() => buildParams(filters, sort), [filters, sort]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([api.get("/applications", { params }), api.get("/applications/stats")]);
      setApps(list.data);
      setStats(st.data);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized();
      else toast.error("Errore di caricamento", { description: apiErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }, [params, onUnauthorized]);

  useEffect(() => {
    const t = setTimeout(load, filters.q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, filters.q]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/applications/${id}`, { status });
      toast.success("Stato aggiornato", { description: STATUS_LABELS[status] });
      setSelected((s) => (s && s.id === id ? { ...s, status } : s));
      load();
    } catch (err) {
      toast.error("Aggiornamento non riuscito", { description: apiErrorMessage(err) });
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/applications/${id}`);
      toast.success("Candidatura eliminata");
      setSelected(null);
      load();
    } catch (err) {
      toast.error("Eliminazione non riuscita", { description: apiErrorMessage(err) });
    }
  };

  const download = async (app) => {
    try {
      const res = await api.get(`/applications/${app.id}/cv`, { responseType: "blob" });
      downloadBlob(res.data, app.cv_filename || "cv.pdf");
    } catch (err) {
      toast.error("Download non riuscito", { description: apiErrorMessage(err) });
    }
  };

  const openPreview = async (app) => {
    const isPdf = (app.cv_filename || "").toLowerCase().endsWith(".pdf");
    setPreview({ app, data: null, loading: isPdf, isPdf });
    if (!isPdf) return;
    try {
      const res = await api.get(`/applications/${app.id}/cv`, { responseType: "arraybuffer" });
      setPreview({ app, data: { data: new Uint8Array(res.data) }, loading: false, isPdf: true });
    } catch (err) {
      setPreview(null);
      toast.error("Anteprima non disponibile", { description: apiErrorMessage(err) });
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { sort: _s, order: _o, ...exportParams } = params;
      const res = await api.get("/applications/export", { params: exportParams, responseType: "blob" });
      downloadBlob(res.data, `candidature_${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success("Esportazione completata");
    } catch (err) {
      toast.error("Esportazione non riuscita", { description: apiErrorMessage(err) });
    } finally {
      setExporting(false);
    }
  };

  const toggleSort = (field) =>
    setSort((s) => (s.field === field ? { field, order: s.order === "desc" ? "asc" : "desc" } : { field, order: "desc" }));

  const actions = { changeStatus, remove, download, preview: openPreview, view: setSelected };

  const statCards = [
    { label: "Candidature totali", value: stats?.totale ?? 0, testid: "app-stat-total" },
    { label: "Nuove", value: stats?.nuova ?? 0, testid: "app-stat-new" },
    { label: "Accettate", value: stats?.accettato ?? 0, testid: "app-stat-accepted" },
    { label: "Media ≥ 27", value: stats?.media_alta ?? 0, testid: "app-stat-high" },
  ];

  return (
    <div data-testid="applications-panel">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{statCards.map((s) => <StatCard key={s.label} {...s} />)}</div>
      <ApplicationsFilters filters={filters} onChange={setFilters} onRefresh={load} onExport={exportCsv} exporting={exporting} />
      <ApplicationsTable apps={apps} loading={loading} sort={sort} onSort={toggleSort} actions={actions} />
      <ApplicationDetailDialog selected={selected} onClose={() => setSelected(null)} actions={actions} />
      <CvPreviewDialog preview={preview} onClose={() => setPreview(null)} onDownload={download} />
    </div>
  );
};
